import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Alert from '../../../../components/ui/Alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { archiveTournament } from '@/api/TournamentManagement/tournamentApi';
import type { TournamentView } from '@/models/tournament';

/**
 * CO-922 (owner decisions 2026-10-21): the old "Delete" button only ever ARCHIVED (ArchiveTournament) while
 * its copy promised to "permanently remove all associated data". It now says what it does
 * (TournamentService.ArchiveTournament): the tournament leaves the lists, its Scheduled / InProgress matches are
 * CANCELLED and archived, and every assignment on them is unlinked (UnlinkAssignmentAsync: linked pay voided, the
 * official's scheduled shift and schedule block archived). Completed matches and their pay stay. No endpoint
 * un-archives a tournament, so this is destructive and cannot be undone (CO-922 review): variant="destructive".
 * Admin and HR. Permanent removal of everything is HR's DeleteTournamentModal.
 */

interface ArchiveTournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: TournamentView | null;
  onArchived?: () => void;
}

const ArchiveTournamentModal: React.FC<ArchiveTournamentModalProps> = ({ isOpen, onClose, tournament, onArchived }) => {
  const { t } = useTranslation('tournament');
  const [isArchiving, setIsArchiving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => { if (isOpen) setApiError(null); }, [isOpen]);

  const handleArchive = async () => {
    if (!tournament) return;
    setIsArchiving(true);
    setApiError(null);
    try {
      const result = await archiveTournament(tournament.id);
      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('archiveTournament.failed'));
        return;
      }
      onArchived?.();
      onClose();
    } catch (error: unknown) {
      const body = error as { errors?: { description?: string }[] } | null;
      setApiError(body?.errors?.[0]?.description || t('archiveTournament.failed'));
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isArchiving) onClose(); }}>
      <DialogContent
        size="sm"
        onEscapeKeyDown={(e) => { if (isArchiving) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isArchiving) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isArchiving) e.preventDefault(); }}
        hideCloseButton={isArchiving}
        data-testid="archive-tournament-modal"
      >
        <DialogHeader className="border-b border-border">
          <DialogTitle className="flex items-center gap-2">
            <Icon name="Archive" size={20} className="shrink-0 text-muted-foreground" />
            {t('archiveTournament.title')}
          </DialogTitle>
          <DialogDescription className="mt-1 break-words font-medium text-foreground">{tournament?.name}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {apiError && (
            <Alert variant="error" role="alert" data-testid="archive-tournament-error">
              <p className="text-sm">{apiError}</p>
            </Alert>
          )}
          <p className="text-sm text-foreground">{t('archiveTournament.body')}</p>
          <p className="text-sm text-muted-foreground">{t('archiveTournament.keepsData')}</p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isArchiving} data-testid="archive-tournament-cancel-btn">
            {t('Cancel')}
          </Button>
          <Button type="button" variant="destructive" onClick={handleArchive} loading={isArchiving} disabled={isArchiving} data-testid="archive-tournament-confirm-btn">
            {t('archiveTournament.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArchiveTournamentModal;
